import { useAuth } from '@tagna/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useEffect, useState } from 'react';

/**
 * Debug component to display current user info and token
 * Use this to verify authentication and check user permissions
 */
export function UserPermissionsDebug() {
  const { user, isAuthenticated, getAccessToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<Record<string, unknown> | null>(null);

  const permissionClaim = decodedToken?.permission;
  const tokenPermissions = Array.isArray(permissionClaim)
    ? permissionClaim.filter((perm): perm is string => typeof perm === 'string')
    : typeof permissionClaim === 'string'
      ? [permissionClaim]
      : [];

  useEffect(() => {
    async function loadToken() {
      const accessToken = await getAccessToken();
      setToken(accessToken);

      if (accessToken) {
        // Decode JWT token (simple base64 decode of payload)
        try {
          const parts = accessToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            setDecodedToken(payload);
          }
        } catch (error) {
          console.error('Error decoding token:', error);
        }
      }
    }

    if (isAuthenticated) {
      loadToken();
    }
  }, [isAuthenticated, getAccessToken]);

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">User Debug Info</h1>
        <p className="text-red-600">Not authenticated</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">User Debug Info</h1>
        <p className="text-muted-foreground">
          Current authentication status and token information
        </p>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle>User Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 font-mono text-sm">
            <div>
              <strong>ID:</strong> {user?.id}
            </div>
            <div>
              <strong>Username:</strong> {user?.userName}
            </div>
            <div>
              <strong>Email:</strong> {user?.email}
            </div>
            <div>
              <strong>Name:</strong> {user?.name}
            </div>
            <div>
              <strong>Roles:</strong> {user?.roles?.join(', ') || 'None'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Token Claims */}
      {decodedToken && (
        <Card>
          <CardHeader>
            <CardTitle>Token Claims (Decoded JWT)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Permissions */}
              {tokenPermissions.length > 0 && (
                <div>
                  <strong className="block mb-2">Permissions:</strong>
                  <div className="bg-muted p-4 rounded-md max-h-96 overflow-auto">
                    <ul className="space-y-1 font-mono text-xs">
                      {tokenPermissions.map((perm, idx) => (
                        <li
                          key={idx}
                          className={perm.includes('Identity.Roles') ? 'text-green-600 font-bold' : ''}
                        >
                          {perm}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* All Claims */}
              <div>
                <strong className="block mb-2">All Token Claims:</strong>
                <pre className="bg-muted p-4 rounded-md text-xs overflow-auto max-h-96">
                  {JSON.stringify(decodedToken, null, 2)}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Access Token */}
      <Card>
        <CardHeader>
          <CardTitle>Access Token</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted p-4 rounded-md overflow-auto">
            <code className="text-xs break-all">{token || 'No token available'}</code>
          </div>
        </CardContent>
      </Card>

      {/* Required Permissions */}
      <Card>
        <CardHeader>
          <CardTitle>Required Permissions for Roles Management</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 font-mono text-sm">
            <li
              className={
                tokenPermissions.some((perm) => perm.includes('Identity.Roles'))
                  ? 'text-green-600'
                  : 'text-red-600'
              }
            >
              <strong>Identity.Roles</strong> (or Identity.Roles.Read) - View roles list
            </li>
            <li
              className={
                tokenPermissions.includes('Identity.Roles.Create')
                  ? 'text-green-600'
                  : 'text-orange-600'
              }
            >
              <strong>Identity.Roles.Create</strong> - Create new roles
            </li>
            <li
              className={
                tokenPermissions.includes('Identity.Roles.Update')
                  ? 'text-green-600'
                  : 'text-orange-600'
              }
            >
              <strong>Identity.Roles.Update</strong> - Edit roles
            </li>
            <li
              className={
                tokenPermissions.includes('Identity.Roles.Delete')
                  ? 'text-green-600'
                  : 'text-orange-600'
              }
            >
              <strong>Identity.Roles.Delete</strong> - Delete roles
            </li>
            <li
              className={
                tokenPermissions.includes('PermissionManagement.PermissionGrants')
                  ? 'text-green-600'
                  : 'text-orange-600'
              }
            >
              <strong>PermissionManagement.PermissionGrants</strong> - Manage permissions
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
